import type { CustomPromiseStateStorage, CustomStateStorage } from '@/state/storage';
import { StorageValue, createJSONStorage, persist } from 'zustand/middleware';
import { StoreApi, UseBoundStore, create } from 'zustand';

import Db from '@/utils/storage';
import { getInitState } from '@/state/utils';

export type ComponentStateType<T> = {
  state: T;
  setState: (value: T | ((current: T) => T)) => void;
};
export type PersistComponentStateType<T> = {
  state: T;
  setState: (value: T | ((current: T) => T)) => void;
  saveState: () => void; // manually save what's in state to storage.
  rehydrate: () => Promise<void>; // manually refresh state to reflect what's in the storage.
  hydrated: boolean; // flag indicating whether state has been hydrated (not persisted)
  setHydrated: (value: boolean) => void; // manually set the hydration flag.
};
export type PersistComponentOptions<T> = {
  live?: boolean; // determines if state should be persisted on change. enabled by default
  storage: (initState: InitComponentStateType<T>, db?: Db) => CustomStateStorage | CustomPromiseStateStorage; // storage to use for persistence.
  reviver?: (key: string, value: unknown) => unknown; // used with JSON.parse to deserialize the data.
  replacer?: (key: string, value: unknown) => unknown; // used with JSON.stringify to serialize the data.
  merge?: (
    persistedState: Partial<PersistComponentStateType<T>>,
    currentState: PersistComponentStateType<T>
  ) => PersistComponentStateType<T>; // merge whats in storage with state.
};

export type InitComponentStateType<T> =
  | T
  | ((setState: (value: T | ((current: T) => T)) => void, getState: () => ComponentStateType<T>) => T | [T, Record<string, Function>]);

// createComponentState - state managed using standard zustand state management (not persisted)
export const createComponentState = <T>(initState: InitComponentStateType<T>): UseBoundStore<StoreApi<ComponentStateType<T>>> =>
  create<ComponentStateType<T>>((set, get): ComponentStateType<T> => getInitState(initState, set, get));

// createPersistentComponentState - state is managed by zustand and persisted via local storage
export const createPersistentComponentState = <T>(
  name: string,
  initState: InitComponentStateType<T>,
  options: PersistComponentOptions<T>
): UseBoundStore<StoreApi<PersistComponentStateType<T>>> => {
  const storage = createJSONStorage(() => options.storage(initState), {
    replacer: options?.replacer, // is a function that is passed to JSON.stringify to serialize the data
    reviver: options?.reviver, // is a function that is passed to JSON.parse to deserialize the data.
  });

  // zustand does a shallow merge by default since we are nesting the state
  // we need to use a custom default merge function to handle deeply nested values
  const merge =
    options?.merge ||
    ((persistedState: Partial<PersistComponentStateType<T>>, currentState) => ({
      ...((persistedState || {}) as Partial<PersistComponentStateType<T>>),
      ...currentState,
      state: {
        ...currentState.state,
        ...((persistedState.state || {}) as T),
      },
    }));

  return create<PersistComponentStateType<T>>()(
    persist(
      (set, get) => ({
        ...getInitState(initState, set, get),
        // internal flag (not persisted) to let consumers know when hydration finished
        hydrated: false,
        setHydrated: (value: boolean) => set((current) => ({ ...current, hydrated: !!value })),
        saveState: (): void => {
          const { state } = get();
          storage.setItem(name, { state: { state } } as StorageValue<PersistComponentStateType<T>>);
        },
        rehydrate: async (): Promise<void> => {
          try {
            const persistedState = (await storage.getItem(name)) as PersistComponentStateType<T>;
            set((current) => merge(persistedState, current));
          } catch (error) {
            console.error(`createPersistentComponentState ${name} - failed to rehydrate:`, error);
          }
        },
      }),
      {
        name,
        storage: {
          getItem: (...args) => storage?.getItem(...args),
          setItem: (...args) => {
            // only persist on state change if live is enabled (enabled by default)
            if (options?.live === false) return;
            return storage?.setItem(...args);
          },
          removeItem: (...args) => storage?.removeItem(...args),
        },
        // Persist only the actual component `state` payload (the T value).
        // This ensures any meta properties (helpers, flags, functions)
        // are never serialized to storage and allows adding new meta
        // fields in the future without touching persisted data.
        partialize: (state) => {
          return { state: (state as PersistComponentStateType<T>).state } as Partial<PersistComponentStateType<T>>;
        },
        onRehydrateStorage: (store) => {
          // `store` is the store API (set/get/etc). The inner callback receives
          // the persisted state (which may be `undefined`). We must call
          // `setHydrated` on the store API, not on the persisted payload.
          return (_persistedState) => {
            try {
              const maybeSetHydrated = (store as any)?.setHydrated;
              // only call if present
              if (typeof maybeSetHydrated === 'function') maybeSetHydrated(true);
            } catch (err) {
              // don't crash on rehydrate
              console.warn(`createPersistentComponentState ${name} onRehydrateStorage failed:`, err);
            }
          };
        },
        merge,
      }
    )
  );
};
